import { useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronsUpDown, Filter, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  DASH,
  dash,
  formatCompactINR,
  formatDate,
  formatNumber,
  monthLabel,
  monthKey,
} from "@/lib/cibil/format";
import type { NormalizedAccount } from "@/lib/cibil/normalize";

type SortKey =
  | "investorName"
  | "loanType"
  | "sanctionDate"
  | "sanctionedLimit"
  | "outstanding"
  | "currentOverdue"
  | "delays"
  | "lastReported"
  | "ownership"
  | "emi"
  | "tenureMonths"
  | "frequency"
  | "collateral"
  | "lastPaymentDate"
  | "lastPaymentAmount"
  | "maxDpd"
  | "maxDpd12"
  | "statusAge"
  | "remarks";

const COLUMNS: { key: SortKey; label: string; numeric?: boolean }[] = [
  { key: "investorName", label: "Investor Name" },
  { key: "loanType", label: "Loan Type" },
  { key: "sanctionDate", label: "Sanction Date" },
  { key: "sanctionedLimit", label: "Sanctioned Limit", numeric: true },
  { key: "outstanding", label: "Outstanding", numeric: true },
  { key: "currentOverdue", label: "Current Overdue", numeric: true },
  { key: "delays", label: "Delays", numeric: true },
  { key: "lastReported", label: "Last Reported" },
  { key: "ownership", label: "Ownership" },
  { key: "emi", label: "EMI", numeric: true },
  { key: "tenureMonths", label: "Tenure", numeric: true },
  { key: "frequency", label: "Frequency" },
  { key: "collateral", label: "Collateral / Security" },
  { key: "lastPaymentDate", label: "Last Payment Date" },
  { key: "lastPaymentAmount", label: "Last Paid Amount", numeric: true },
  { key: "maxDpd", label: "Worst DPD", numeric: true },
  { key: "maxDpd12", label: "Worst DPD (12m)", numeric: true },
  { key: "statusAge", label: "Status Age", numeric: true },
  { key: "remarks", label: "Account Remarks" },
];

/** Worst DPD reported in the trailing 12 months from today. */
function worstDpd12(a: NormalizedAccount): number | null {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - 12);
  const vals = a.payments
    .filter((p) => p.date >= cutoff && p.dpd && p.dpd > 0)
    .map((p) => p.dpd as number);
  return vals.length ? Math.max(...vals) : null;
}

/** Days since the account status was last set / reported. */
function statusAgeDays(a: NormalizedAccount): number | null {
  const d = a.statusDate ?? a.lastReported;
  if (!d) return null;
  return Math.max(0, Math.round((Date.now() - d.getTime()) / 86_400_000));
}

const ADVERSE = ["Special Mention Account", "Substandard", "Doubtful", "Loss"];

const isAdverse = (a: NormalizedAccount) =>
  (a.worstClassification !== null && ADVERSE.includes(a.worstClassification)) ||
  a.remarks.some((r) => /written off|settled|suit filed|wilful default/i.test(r));

/** Circular multi-select control matching the Insight filter panel. */
function FilterCheck({
  checked,
  onToggle,
  children,
}: {
  checked: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-800">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        onClick={onToggle}
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-violet-500 transition-colors",
          checked && "bg-violet-500",
        )}
      >
        {checked ? <span className="size-1.5 rounded-full bg-white" /> : null}
      </button>
      <span>{children}</span>
    </label>
  );
}

interface Filters {
  loanTypes: string[];
  ownerships: string[];
  security: "all" | "secured" | "unsecured";
  onlyAdverse: boolean;
  onlyWithDpd: boolean;
  minAmount: string;
  maxAmount: string;
}

const EMPTY_FILTERS: Filters = {
  loanTypes: [],
  ownerships: [],
  security: "all",
  onlyAdverse: false,
  onlyWithDpd: false,
  minAmount: "",
  maxAmount: "",
};

function sortValue(a: NormalizedAccount, key: SortKey): string | number {
  switch (key) {
    case "sanctionDate":
      return a.sanctionDate?.getTime() ?? 0;
    case "lastReported":
      return a.lastReported?.getTime() ?? 0;
    case "delays":
      return a.dpdCount;
    case "remarks":
      return a.remarks.join(", ").toLowerCase();
    case "lastPaymentDate":
      return a.lastPaymentDate?.getTime() ?? 0;
    case "lastPaymentAmount":
      return a.lastPaymentAmount ?? -1;
    case "maxDpd":
      return a.maxDpd ?? -1;
    case "maxDpd12":
      return worstDpd12(a) ?? -1;
    case "statusAge":
      return statusAgeDays(a) ?? -1;
    case "sanctionedLimit":
    case "outstanding":
    case "currentOverdue":
    case "emi":
    case "tenureMonths":
      return a[key] ?? -1;
    default:
      return (a[key] ?? "").toString().toLowerCase();
  }
}

function DelaysCell({ account }: { account: NormalizedAccount }) {
  if (account.dpdCount === 0) {
    return (
      <Badge className="bg-success-soft text-success" variant="secondary">
        On time
      </Badge>
    );
  }
  const late = account.payments.filter((p) => p.dpd && p.dpd > 0).slice(-12).reverse();
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className="cursor-default bg-danger-soft text-destructive" variant="secondary">
            {formatNumber(account.dpdCount)} · max {formatNumber(account.maxDpd)}d
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-60">
          <p className="mb-1 font-medium">Delayed months</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
            {late.map((p) => (
              <span key={p.date.toISOString()}>
                {monthLabel(monthKey(p.date))}: {p.dpd}d
              </span>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AccountsSection({
  title,
  accounts,
  defaultOpen,
}: {
  title: string;
  accounts: NormalizedAccount[];
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "sanctionDate",
    dir: "desc",
  });

  const rows = useMemo(() => {
    const copy = [...accounts];
    copy.sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      if (va === vb) return 0;
      const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va) > String(vb) ? 1 : -1;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [accounts, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left hover:bg-muted/50"
      >
        <span className="font-display text-sm font-semibold">
          {title}{" "}
          <span className="text-muted-foreground">
            [{formatNumber(accounts.length)} Accounts]
          </span>
        </span>
        <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div className="overflow-x-auto border-t border-border">
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableHead
                    key={c.key}
                    className={cn("whitespace-nowrap", c.numeric && "text-right")}
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key)}
                      className={cn(
                        "inline-flex items-center gap-1 hover:text-foreground",
                        sort.key === c.key && "text-foreground",
                      )}
                    >
                      {c.label}
                      <ChevronsUpDown className="size-3 opacity-60" />
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} className="py-8 text-center text-muted-foreground">
                    No accounts match the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {dash(a.investorName)}
                      {a.accountNumber ? (
                        <span className="block text-xs font-normal text-muted-foreground">
                          {a.accountNumber}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{dash(a.loanType)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(a.sanctionDate)}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatCompactINR(a.sanctionedLimit)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatCompactINR(a.outstanding)}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right tabular-nums whitespace-nowrap",
                        (a.currentOverdue ?? 0) > 0 && "font-semibold text-destructive",
                      )}
                    >
                      {formatCompactINR(a.currentOverdue)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DelaysCell account={a} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(a.lastReported)}</TableCell>
                    <TableCell className="whitespace-nowrap">{dash(a.ownership)}</TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {formatCompactINR(a.emi)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {a.tenureMonths === null ? DASH : `${a.tenureMonths} mo`}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{dash(a.frequency)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {a.collateral ?? (a.isSecured ? "Secured" : "Not reported")}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(a.lastPaymentDate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {a.lastPaymentAmount === null ? DASH : formatCompactINR(a.lastPaymentAmount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {a.maxDpd === null ? DASH : `${formatNumber(a.maxDpd)}d`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {worstDpd12(a) === null ? DASH : `${formatNumber(worstDpd12(a)!)}d`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums whitespace-nowrap">
                      {statusAgeDays(a) === null ? DASH : `${formatNumber(statusAgeDays(a)!)}d`}
                    </TableCell>
                    <TableCell className="min-w-40">
                      {a.remarks.length === 0 ? (
                        DASH
                      ) : (
                        <div className="flex max-w-72 flex-wrap gap-1">
                          {a.remarks.map((r) => (
                            <Badge
                              key={r}
                              variant="outline"
                              title={r}
                              className="max-w-full truncate text-xs font-normal"
                            >
                              {r}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </Card>
  );
}

export function TableView({ accounts }: { accounts: NormalizedAccount[] }) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  const loanTypes = useMemo(
    () => [...new Set(accounts.map((a) => a.loanType ?? "Unknown"))].sort(),
    [accounts],
  );
  const ownerships = useMemo(
    () => [...new Set(accounts.map((a) => a.ownership ?? "Unknown"))].sort(),
    [accounts],
  );

  const filtered = useMemo(() => {
    const min = filters.minAmount === "" ? null : Number(filters.minAmount);
    const max = filters.maxAmount === "" ? null : Number(filters.maxAmount);
    return accounts.filter((a) => {
      if (filters.loanTypes.length && !filters.loanTypes.includes(a.loanType ?? "Unknown")) return false;
      if (filters.ownerships.length && !filters.ownerships.includes(a.ownership ?? "Unknown")) return false;
      if (filters.onlyWithDpd && a.dpdCount === 0) return false;
      if (filters.security === "secured" && !a.isSecured) return false;
      if (filters.security === "unsecured" && a.isSecured) return false;
      if (filters.onlyAdverse && !isAdverse(a)) return false;
      const amount = a.sanctionedLimit ?? 0;
      if (min !== null && Number.isFinite(min) && amount < min) return false;
      if (max !== null && Number.isFinite(max) && amount > max) return false;
      return true;
    });
  }, [accounts, filters]);

  const activeFilterCount =
    filters.loanTypes.length +
    filters.ownerships.length +
    (filters.onlyWithDpd ? 1 : 0) +
    (filters.onlyAdverse ? 1 : 0) +
    (filters.security === "all" ? 0 : 1) +
    (filters.minAmount ? 1 : 0) +
    (filters.maxAmount ? 1 : 0);

  const toggleIn = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Showing {formatNumber(filtered.length)} of {formatNumber(accounts.length)} accounts
        </p>
        <div className="flex items-center gap-2">
          {activeFilterCount > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY_FILTERS)}>
              <X className="size-4" /> Clear
            </Button>
          ) : null}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <Filter className="size-4" /> Add Filters
                {activeFilterCount > 0 ? (
                  <Badge variant="secondary" className="ml-1">
                    {activeFilterCount}
                  </Badge>
                ) : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[300px] max-h-[70vh] overflow-y-auto rounded-xl border-slate-200 p-4 shadow-lg"
            >
              <div className="grid gap-5">
                <div className="grid gap-2.5">
                  <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">
                    Loan Type
                  </Label>
                  {loanTypes.map((t) => (
                    <FilterCheck
                      key={t}
                      checked={filters.loanTypes.includes(t)}
                      onToggle={() =>
                        setFilters((f) => ({ ...f, loanTypes: toggleIn(f.loanTypes, t) }))
                      }
                    >
                      {t}
                    </FilterCheck>
                  ))}
                </div>
                <div className="grid gap-2.5">
                  <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">
                    Ownership
                  </Label>
                  {ownerships.map((t) => (
                    <FilterCheck
                      key={t}
                      checked={filters.ownerships.includes(t)}
                      onToggle={() =>
                        setFilters((f) => ({ ...f, ownerships: toggleIn(f.ownerships, t) }))
                      }
                    >
                      {t}
                    </FilterCheck>
                  ))}
                </div>
                <div className="grid gap-2.5">
                  <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">
                    Security
                  </Label>
                  <div className="flex gap-2">
                    {(
                      [
                        { value: "all", label: "All" },
                        { value: "secured", label: "Secured" },
                        { value: "unsecured", label: "Unsecured" },
                      ] as const
                    ).map((s2) => {
                      const active = filters.security === s2.value;
                      return (
                        <button
                          key={s2.value}
                          type="button"
                          onClick={() => setFilters((f) => ({ ...f, security: s2.value }))}
                          className={cn(
                            "h-8 flex-1 rounded-md text-xs font-medium transition-colors",
                            active
                              ? "bg-violet-600 text-white"
                              : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                          )}
                        >
                          {s2.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid gap-2.5">
                  <FilterCheck
                    checked={filters.onlyAdverse}
                    onToggle={() => setFilters((f) => ({ ...f, onlyAdverse: !f.onlyAdverse }))}
                  >
                    Only adverse classification / status
                  </FilterCheck>
                  <FilterCheck
                    checked={filters.onlyWithDpd}
                    onToggle={() => setFilters((f) => ({ ...f, onlyWithDpd: !f.onlyWithDpd }))}
                  >
                    Only accounts with DPDs
                  </FilterCheck>
                </div>
                <div className="grid gap-2.5">
                  <Label className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">
                    Sanctioned amount (₹)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      inputMode="numeric"
                      placeholder="Min"
                      value={filters.minAmount}
                      onChange={(e) => setFilters((f) => ({ ...f, minAmount: e.target.value }))}
                      className="h-9 rounded-md border-slate-200"
                    />
                    <Input
                      inputMode="numeric"
                      placeholder="Max"
                      value={filters.maxAmount}
                      onChange={(e) => setFilters((f) => ({ ...f, maxAmount: e.target.value }))}
                      className="h-9 rounded-md border-slate-200"
                    />
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <AccountsSection
        title="Active Accounts"
        accounts={filtered.filter((a) => a.isActive)}
        defaultOpen
      />
      <AccountsSection
        title="Closed Accounts"
        accounts={filtered.filter((a) => !a.isActive)}
        defaultOpen={false}
      />
    </div>
  );
}
